import React from "react";
import { Link } from "react-router-dom";
import ReviewsSlider from "../components/ReviewsSlider";
import ContactForm from "../components/ContactForm";
import GalleryViewer from "../components/GalleryViewer";
import HeroQuickContact from "../components/HeroQuickContact";
import SEO from "../components/SEO";
import { CITIES } from "../data/cities";

export default function Home() {
  return (
    <div className="bg-[#FAF9F6] text-stone-800">
      <SEO
        title="Energikonsulent i Jylland & Fyn | Ring i dag"
        description="Mangler du en erfaren energikonsulent, står vi klar med teknisk ekspertise og løsninger skræddersyet til dine behov. Kontakt os her."
        canonical="https://www.etdanmark.com/"
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07506.jpg"
      />
      {/* 1. HERO SECTION WITH FULL-VIEW BACKGROUND IMAGE */}
      <section className="relative pt-36 sm:pt-40 pb-18 lg:pt-44 lg:pb-24 overflow-hidden border-b border-stone-200/70">
        {/* Full-view background image with lightened overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07506.jpg"
            srcSet="
              https://assets.auxodev.dk/cdn-cgi/image/width=640,quality=75,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07506.jpg 640w,
              https://assets.auxodev.dk/cdn-cgi/image/width=1024,quality=80,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07506.jpg 1024w,
              https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07506.jpg 1600w,
              https://assets.auxodev.dk/cdn-cgi/image/width=2000,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07506.jpg 2000w
            "
            sizes="100vw"
            fetchPriority="high"
            loading="eager"
            decoding="async"
            alt="ET Danmark energikonsulent ved rådgivning"
            className="w-full h-full object-cover object-center"
          />
          {/* Transparent, gentle tint so background is clearly visible while keeping text readable */}
          <div className="absolute inset-0 bg-stone-950/35 backdrop-brightness-95 bg-gradient-to-r from-stone-950/60 via-stone-950/30 to-stone-950/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left Content (7 cols) */}
            <div className="lg:col-span-7 space-y-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.12]">
                Energikonsulenter i <span className="inline sm:block">Jylland & Fyn</span>
              </h1>

              <p className="text-lg sm:text-xl text-stone-200 font-normal leading-relaxed max-w-2xl">
                Energimærkning, rådgivning, energiramme, trykprøvning, termografi m.m...
              </p>

              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5">
                <Link
                  to="/prisberegner"
                  className="px-7 py-4 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white font-bold text-base shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2.5 group"
                >
                  <i className="fa-solid fa-calculator text-emerald-300 group-hover:scale-110 transition-transform" aria-hidden="true"></i>
                  <span>Beregn din pris online</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>

                <a
                  href="tel:+4542499595"
                  className="px-6 py-4 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-xs border border-white/25 text-white font-bold text-base shadow-sm transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-emerald-300" aria-hidden="true"></i>
                  <span className="whitespace-nowrap">Ring 42 49 95 95</span>
                </a>
              </div>

              {/* Trust badges */}
              <div className="pt-6 border-t border-white/15 grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs font-medium text-stone-200">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-emerald-400 text-sm" aria-hidden="true"></i>
                  <span>20 års byggeerfaring</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-emerald-400 text-sm" aria-hidden="true"></i>
                  <span>Hurtig rapportlevering</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-emerald-400 text-sm" aria-hidden="true"></i>
                  <span>Gennemskuelige fra-priser</span>
                </div>
              </div>
            </div>

            {/* Right Quick Contact Form (5 cols) */}
            <div className="lg:col-span-5">
              <HeroQuickContact />
            </div>
          </div>
        </div>
      </section>

      {/* 2. KONKURRENCEDYGTIGE PRISER (Pricing Snapshot) */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 uppercase tracking-wider mb-2">
              <i className="fa-solid fa-tag text-emerald-600" aria-hidden="true"></i>
              Gennemskuelig prissætning
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Konkurrencedygtige priser
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-2">
              Hos ET Danmark ApS ved du altid, hvad du betaler for. Ingen skjulte gebyrer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {/* Energimærkning */}
            <div className="rounded-2xl p-6 border-2 border-stone-200/80 bg-white flex flex-col justify-between hover:border-emerald-300 transition-all shadow-sm">
              <div>
                <div className="rounded-xl overflow-hidden mb-5 aspect-4/3 bg-stone-100 border border-stone-200/60">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07576-2.jpg"
                    alt="Energimærkning"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold text-stone-900">Energimærkning</h3>
                <p className="text-xs text-stone-500 mt-1">
                  Lovpligtig rapport ved salg og udlejning af bolig.
                </p>
                <div className="mt-4 pb-4 border-b border-stone-100">
                  <span className="text-xs text-stone-500 block">Vejledende pris</span>
                  <span className="text-3xl font-extrabold text-[#165B4C] block mt-0.5">
                    Fra 4.188 kr.
                  </span>
                  <span className="text-xs text-stone-500">inkl. moms</span>
                </div>
                <ul className="mt-4 space-y-2 text-xs text-stone-700">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-600" aria-hidden="true"></i>
                    <span>Grundig bygningsgennemgang</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-600" aria-hidden="true"></i>
                    <span>Officiel rapport gyldig i 10 år</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-600" aria-hidden="true"></i>
                    <span>Konkrete forslag til energioptimering</span>
                  </li>
                </ul>
              </div>
              <div className="mt-6 pt-4 border-t border-stone-100">
                <Link
                  to="/energimaerkning"
                  className="w-full block py-2.5 text-center rounded-full bg-stone-50 border border-stone-300 hover:bg-[#165B4C] hover:text-white hover:border-[#165B4C] text-stone-800 font-semibold text-xs transition-colors"
                >
                  Læs mere om Energimærkning
                </Link>
              </div>
            </div>

            {/* Trykprøvning / BlowerDoor (Highlighted) */}
            <div className="rounded-2xl p-6 border-2 border-[#165B4C] bg-white shadow-lg flex flex-col justify-between relative">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-[#165B4C] text-white text-[11px] font-bold uppercase tracking-wider rounded-full shadow-xs whitespace-nowrap">
                Mest efterspurgt til nybyg
              </div>
              <div>
                <div className="rounded-xl overflow-hidden mb-5 aspect-4/3 bg-stone-100 border border-stone-200/60">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07328-2.jpg"
                    alt="Trykprøvning / BlowerDoor"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold text-stone-900">Trykprøvning / BlowerDoor</h3>
                <p className="text-xs text-stone-500 mt-1">
                  Måling af lufttæthed til bygningsreglement & komfort.
                </p>
                <div className="mt-4 pb-4 border-b border-stone-100">
                  <span className="text-xs text-stone-500 block">Vejledende pris</span>
                  <span className="text-3xl font-extrabold text-[#165B4C] block mt-0.5">
                    Fra 3.300 kr.
                  </span>
                  <span className="text-xs text-stone-500">inkl. moms</span>
                </div>
                <ul className="mt-4 space-y-2 text-xs text-stone-700">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-600" aria-hidden="true"></i>
                    <span>Præcis kalibreret BlowerDoor test</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-600" aria-hidden="true"></i>
                    <span>Dokumentation til kommunen</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-600" aria-hidden="true"></i>
                    <span>Lokalisering af træk og utætheder</span>
                  </li>
                </ul>
              </div>
              <div className="mt-6 pt-4 border-t border-stone-100">
                <Link
                  to="/trykproevning"
                  className="w-full block py-2.5 text-center rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white font-semibold text-xs transition-colors shadow"
                >
                  Læs mere om Trykprøvning
                </Link>
              </div>
            </div>

            {/* Termografi */}
            <div className="rounded-2xl p-6 border-2 border-stone-200/80 bg-white flex flex-col justify-between hover:border-emerald-300 transition-all shadow-sm">
              <div>
                <div className="rounded-xl overflow-hidden mb-5 aspect-4/3 bg-stone-100 border border-stone-200/60">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07476-2-0.jpg"
                    alt="Termografi med varmekamera"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold text-stone-900">Termografi</h3>
                <p className="text-xs text-stone-500 mt-1">
                  Infrarød visualisering af varmetab og kuldebroer.
                </p>
                <div className="mt-4 pb-4 border-b border-stone-100">
                  <span className="text-xs text-stone-500 block">Vejledende pris</span>
                  <span className="text-3xl font-extrabold text-[#165B4C] block mt-0.5">
                    Fra 2.500 kr.
                  </span>
                  <span className="text-xs text-stone-500">inkl. moms</span>
                </div>
                <ul className="mt-4 space-y-2 text-xs text-stone-700">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-600" aria-hidden="true"></i>
                    <span>High-end termografisk kamera</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-600" aria-hidden="true"></i>
                    <span>Visuel dokumentation af kuldebroer</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-600" aria-hidden="true"></i>
                    <span>Effektivt før eller efter renovering</span>
                  </li>
                </ul>
              </div>
              <div className="mt-6 pt-4 border-t border-stone-100">
                <Link
                  to="/termografi"
                  className="w-full block py-2.5 text-center rounded-full bg-stone-50 border border-stone-300 hover:bg-[#165B4C] hover:text-white hover:border-[#165B4C] text-stone-800 font-semibold text-xs transition-colors"
                >
                  Læs mere om Termografi
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/prisberegner"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 text-emerald-800 text-sm font-semibold transition-colors"
            >
              <i className="fa-solid fa-calculator text-xs" aria-hidden="true"></i>
              <span>Beregn samlet pris for din bolig i Prisberegneren &rarr;</span>
            </Link>
          </div>
        </div>
      </section>

      {/* 3. OM OS / 20 ÅRS ERFARING (LARS) */}
      <section className="py-16 sm:py-20 bg-stone-50 border-t border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 relative">
              <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white aspect-4/3 sm:aspect-square bg-stone-200">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07601-2_f9fcce3b.jpg"
                  alt="Lars energikonsulent ved trykprøvning i Jylland og Fyn"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -right-5 w-32 h-32 sm:w-36 sm:h-36 rounded-full bg-[#165B4C] text-white border-4 border-white shadow-xl hidden sm:flex flex-col items-center justify-center text-center p-3">
                <span className="text-2xl sm:text-3xl font-extrabold leading-none tracking-tight">20+</span>
                <span className="text-[11px] sm:text-xs text-emerald-100 font-medium leading-tight mt-1">
                  Års erfaring i byggebranchen
                </span>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-100/70 text-emerald-800 text-xs font-semibold uppercase tracking-wider">
                <span>Personlig & Uvildig Rådgivning</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
                Mød din energikonsulent
              </h2>

              <blockquote className="p-5 rounded-2xl bg-white border border-stone-200 text-stone-700 text-sm sm:text-base leading-relaxed italic">
                "Som energikonsulent har vi over 20 års erfaring i byggebranchen, med et bredt kendskab til hele byggeriet, gennem mange års selvstændig virke med totalentrepriser. Vores passion er at give kunderne, både store og små, en god oplevelse og kvalitet for pengene. Vi bestræber os på at yde den bedste service i branchen, og værner om de faste samarbejdspartnere."
              </blockquote>

              <p className="text-sm text-stone-600 leading-relaxed">
                Uanset om du skal sælge et parcelhus i Jylland eller på Fyn, bygge nyt sommerhus ved kysten eller har brug for en energiramme til erhverv, er du i trygge hænder hos ET Danmark ApS.
              </p>

              <div className="pt-2 flex flex-wrap gap-4">
                <Link
                  to="/om-os"
                  className="px-6 py-2.5 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white font-semibold text-sm transition-colors"
                >
                  Læs mere om os &rarr;
                </Link>
                <a
                  href="tel:+4542499595"
                  className="px-6 py-2.5 rounded-full bg-white border border-stone-300 hover:bg-stone-100 text-stone-800 font-semibold text-sm transition-colors whitespace-nowrap"
                >
                  Ring til Lars: 42 49 95 95
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. SOCIAL PROOF (ANMELDELSER) - PLACERET OVER YDELSER */}
      <ReviewsSlider />

      {/* 5. YDELSER OVERSIGT ("Ingen opgaver er for små eller for store") */}
      <section className="py-16 sm:py-20 bg-white border-t border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 uppercase tracking-wider mb-2">
              <i className="fa-solid fa-briefcase text-emerald-600" aria-hidden="true"></i>
              Komplet serviceprogram
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Ingen opgaver er for små eller for store
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-2">
              Vi servicerer både private boligejere, entreprenører, arkitekter og erhvervsvirksomheder i hele Jylland og Fyn.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {/* 1. Energiramme */}
            <div className="rounded-2xl overflow-hidden bg-white border border-stone-200/80 shadow-2xs flex flex-col justify-between hover:border-emerald-300 transition-all">
              <div>
                <div className="aspect-16/10 bg-stone-100 overflow-hidden">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07546.jpg"
                    alt="Energiramme"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-stone-900">Energiramme</h3>
                  <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                    Beregning af maksimalt energiforbrug pr. m² pr. år til byggeansøgning og bygningsreglement.
                  </p>
                </div>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/energiramme"
                  className="w-full py-2.5 px-4 rounded-full bg-stone-50 hover:bg-[#165B4C] hover:text-white text-xs font-bold text-[#165B4C] flex items-center justify-between border border-stone-200 transition-colors"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            {/* 2. LCA-beregning */}
            <div className="rounded-2xl overflow-hidden bg-white border border-stone-200/80 shadow-2xs flex flex-col justify-between hover:border-emerald-300 transition-all">
              <div>
                <div className="aspect-16/10 bg-stone-100 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07384.jpg"
                    alt="LCA-beregning"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                  <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-700 text-white shadow-xs">
                    Nyhed
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-stone-900">LCA-beregning</h3>
                  <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                    Lovpligtig klimaberegning af nybyggeri efter BR18 – samlet med energiramme og trykprøve.
                  </p>
                  <p className="text-xs font-bold text-[#165B4C] mt-2">
                    Vejledende pris: Fra 7.500 kr. inkl. moms
                  </p>
                </div>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/lca-beregning"
                  className="w-full py-2.5 px-4 rounded-full bg-stone-50 hover:bg-[#165B4C] hover:text-white text-xs font-bold text-[#165B4C] flex items-center justify-between border border-stone-200 transition-colors"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            {/* 3. Energimærkning */}
            <div className="rounded-2xl overflow-hidden bg-white border border-stone-200/80 shadow-2xs flex flex-col justify-between hover:border-emerald-300 transition-all">
              <div>
                <div className="aspect-16/10 bg-stone-100 overflow-hidden">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07467.jpg"
                    alt="Energimærkning"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-stone-900">Energimærkning</h3>
                  <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                    Autoriseret vurdering af bygningens energiforbrug fra A2020 til G med konkrete besparelsesforslag.
                  </p>
                </div>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/energimaerkning"
                  className="w-full py-2.5 px-4 rounded-full bg-stone-50 hover:bg-[#165B4C] hover:text-white text-xs font-bold text-[#165B4C] flex items-center justify-between border border-stone-200 transition-colors"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            {/* 3. Trykprøvning */}
            <div className="rounded-2xl overflow-hidden bg-white border border-stone-200/80 shadow-2xs flex flex-col justify-between hover:border-emerald-300 transition-all">
              <div>
                <div className="aspect-16/10 bg-stone-100 overflow-hidden">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07369.jpg"
                    alt="Trykprøvning"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-stone-900">Trykprøvning</h3>
                  <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                    BlowerDoor test der opdager utætheder, træk og varmetab for at sikre et sundt indeklima.
                  </p>
                </div>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/trykproevning"
                  className="w-full py-2.5 px-4 rounded-full bg-stone-50 hover:bg-[#165B4C] hover:text-white text-xs font-bold text-[#165B4C] flex items-center justify-between border border-stone-200 transition-colors"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            {/* 4. Termografi */}
            <div className="rounded-2xl overflow-hidden bg-white border border-stone-200/80 shadow-2xs flex flex-col justify-between hover:border-emerald-300 transition-all">
              <div>
                <div className="aspect-16/10 bg-stone-100 overflow-hidden">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07589.webp"
                    alt="Termografi"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-stone-900">Termografi</h3>
                  <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                    Termografisk undersøgelse med infrarødt kamera, der afslører skjulte isoleringsfejl og kuldebroer.
                  </p>
                </div>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/termografi"
                  className="w-full py-2.5 px-4 rounded-full bg-stone-50 hover:bg-[#165B4C] hover:text-white text-xs font-bold text-[#165B4C] flex items-center justify-between border border-stone-200 transition-colors"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            {/* 6. Fugtmåling */}
            <div className="rounded-2xl overflow-hidden bg-white border border-stone-200/80 shadow-2xs flex flex-col justify-between hover:border-emerald-300 transition-all">
              <div>
                <div className="aspect-16/10 bg-stone-100 overflow-hidden relative">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07546.jpg"
                    alt="Fugtmåling"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                  <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-700 text-white shadow-xs">
                    Nyhed
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-stone-900">Fugtmåling</h3>
                  <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                    Find fugt i tag, vægge, gulve og træ – ikke-destruktivt med professionelt Tramex-udstyr.
                  </p>
                  <p className="text-xs font-bold text-[#165B4C] mt-2">
                    Vejledende pris: Fra 5.000 kr. inkl. moms
                  </p>
                  <p className="text-[11px] text-stone-500 mt-0.5">
                    Rapport inden for 2–5 hverdage efter målingen.
                  </p>
                </div>
              </div>
              <div className="p-5 pt-0">
                <Link
                  to="/fugtmaaling"
                  className="w-full py-2.5 px-4 rounded-full bg-stone-50 hover:bg-[#165B4C] hover:text-white text-xs font-bold text-[#165B4C] flex items-center justify-between border border-stone-200 transition-colors"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. FORDELE VED AT VÆLGE EN ERFAREN ENERGIKONSULENT I JYLLAND OG FYN */}
      <section className="py-16 sm:py-20 bg-stone-50 border-t border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Fordele ved at vælge en erfaren energikonsulent i Jylland og Fyn
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-3 leading-relaxed">
              Investering i energirådgivning er ikke kun en økonomisk fordelagtig beslutning, men også et skridt mod en mere bæredygtig fremtid. Her er en detaljeret gennemgang af væsentlige fordele ved at vælge professionel energirådgivning:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Reducerede energiomkostninger */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center mb-4">
                <i className="fa-solid fa-wallet" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">Reducerede energiomkostninger</h3>
              <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                En energikonsulent identificerer de største energiomkostninger i din bolig eller virksomhed, og foreslår effektive løsninger til at reducere energiforbruget.
              </p>
            </div>

            {/* Forbedret indeklima */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center mb-4">
                <i className="fa-solid fa-lungs" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">Forbedret indeklima</h3>
              <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                Optimering af opvarmning og ventilation, fører til et sundere indeklima.
              </p>
            </div>

            {/* Øget ejendomsværdi */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center mb-4">
                <i className="fa-solid fa-chart-line-up" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">Øget ejendomsværdi</h3>
              <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                Opdaterede energisystemer og bedre isolering kan øge din ejendoms markedspris.
              </p>
            </div>

            {/* Miljømæssige fordele */}
            <div className="bg-white p-6 rounded-2xl border border-stone-200 shadow-2xs">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center mb-4">
                <i className="fa-solid fa-seedling" aria-hidden="true"></i>
              </div>
              <h3 className="font-bold text-stone-900 text-base">Miljømæssige fordele</h3>
              <p className="text-xs text-stone-600 mt-2 leading-relaxed">
                Ved at reducere energiforbruget bidrager du positivt til miljøet ved at sænke dit CO2-aftryk.
              </p>
            </div>
          </div>

          <div className="mt-8 p-6 rounded-2xl bg-white border border-stone-200 text-sm text-stone-700 leading-relaxed">
            <p>
              Som energikonsulenter i Jylland og Fyn arbejder vi tæt sammen med dig for at sikre bæredygtige og omkostningseffektive resultater. Med vores dedikation og ekspertise vil vi guide dig igennem alle faser af energioptimering, og sikre din tilfredshed fra start til slut. Du er altid velkommen til at kontakte os, hvis du ønsker at høre mere om hvordan du kan opnå ovenstående fordele, eller bestille en tid til optimering af energien i din bolig, virksomhed eller institution.
            </p>
          </div>
        </div>
      </section>

      {/* 6. TERMOGRAFI SPOTLIGHT */}
      <section className="py-16 sm:py-20 bg-white border-t border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 space-y-5">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-100/70 text-emerald-800 text-xs font-semibold uppercase tracking-wider">
                <span>Infrarød teknologi</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
                Se det skjulte med termografi
              </h2>

              <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                Med termografi får du et tydeligt billede af, hvor varmen forsvinder fra din bolig eller virksomhed. Vores varmekamera afslører kuldebroer, utætheder og dårlig isolering – problemer, der ellers er usynlige for øjet. En termografisk undersøgelse giver dig:
              </p>

              <ul className="space-y-3 pt-2 text-sm text-stone-800 font-medium">
                <li className="flex items-center gap-3 p-3 rounded-xl bg-stone-50 border border-stone-200/80">
                  <span className="text-emerald-600 font-bold">✔️</span>
                  <span>Klar dokumentation på bygningens tilstand</span>
                </li>
                <li className="flex items-center gap-3 p-3 rounded-xl bg-stone-50 border border-stone-200/80">
                  <span className="text-emerald-600 font-bold">✔️</span>
                  <span>Mulighed for at spare på varmeregningen</span>
                </li>
                <li className="flex items-center gap-3 p-3 rounded-xl bg-stone-50 border border-stone-200/80">
                  <span className="text-emerald-600 font-bold">✔️</span>
                  <span>Bedre komfort og indeklima</span>
                </li>
              </ul>

              <p className="text-xs text-stone-500 italic">
                Kontakt os i dag og få en professionel termografi af din bygning – så du ved præcis, hvor du skal sætte ind.
              </p>

              <div className="pt-2 flex flex-wrap gap-4">
                <Link
                  to="/termografi"
                  className="px-6 py-3 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white font-semibold text-sm transition-colors"
                >
                  Læs mere om Termografi &rarr;
                </Link>
                <Link
                  to="/prisberegner"
                  className="px-6 py-3 rounded-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 text-emerald-800 font-semibold text-sm transition-colors"
                >
                  Beregn pris på termografi
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white aspect-4/3 bg-stone-100">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07564-2.jpg"
                  alt="Termografisk måling med varmekamera på bolig"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. DÆKNINGSOMRÅDE (Få hjælp i din by) */}
      <section className="py-14 sm:py-18 bg-emerald-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Få hjælp i din by
          </h2>
          <p className="text-emerald-200 text-sm max-w-xl mx-auto mt-2 mb-8">
            Vi arbejder også som energikonsulenter i følgende byer og omkringliggende områder i hele Jylland og Fyn:
          </p>

          <div className="flex flex-wrap justify-center gap-2.5 max-w-4xl mx-auto">
            {CITIES.map((city) => (
              <Link
                key={city.slug}
                to={`/energikonsulent-i-${city.slug}`}
                className="px-4 py-2 rounded-xl bg-emerald-950/80 border border-emerald-700/50 text-sm font-semibold hover:bg-emerald-800 hover:border-emerald-500/70 transition-all text-white shadow-2xs inline-flex items-center gap-1.5"
              >
                <span>{city.name}</span>
                <i className="fa-solid fa-arrow-right text-[10px] text-emerald-400 opacity-70" aria-hidden="true"></i>
              </Link>
            ))}
          </div>
          <p className="text-xs text-emerald-300 mt-6">
            Uanset hvor du bor i Region Midtjylland, Nordjylland eller Syddanmark, rykker vi hurtigt ud.
          </p>
        </div>
      </section>

      {/* 10. KONTAKT SEKTION */}
      <section className="py-16 sm:py-24 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Col (5 cols): Callout & Info */}
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-100/70 text-emerald-800 text-xs font-semibold uppercase tracking-wider">
                <span>Hurtig kontakt</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
                Få hjælp fra en erfaren energikonsulent i Jylland og Fyn i dag
              </h2>

              <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                Er du klar til at tage det næste skridt mod en mere energieffektiv fremtid i Jylland eller på Fyn? Så er en energikonsulent det rette valg. Kontakt os i dag for en uforpligtende snak om dine muligheder.
              </p>

              <div className="p-6 rounded-2xl bg-white border border-stone-200/90 shadow-2xs space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-phone" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-xs text-stone-500 block">Direkte telefon:</span>
                    <a
                      href="tel:+4542499595"
                      className="font-bold text-stone-900 hover:text-emerald-700 text-base whitespace-nowrap"
                    >
                      42 49 95 95
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-envelope" aria-hidden="true"></i>
                  </div>
                  <div>
                    <span className="text-xs text-stone-500 block">E-mail:</span>
                    <a
                      href="mailto:info@etdanmark.com"
                      className="font-bold text-stone-900 hover:text-emerald-700 text-sm whitespace-nowrap"
                    >
                      info@etdanmark.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
                  </div>
                  <div className="text-xs text-stone-600">
                    <p className="font-semibold text-stone-900">ET Danmark ApS</p>
                    <p>Bjerrevej 62 A, 8700 Horsens</p>
                    <p className="text-[11px] text-stone-400 mt-1">CVR: 41026138</p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-stone-500 leading-relaxed">
                Du kan fange os på telefon <a href="tel:+4542499595" className="font-bold text-stone-800 whitespace-nowrap">42 49 95 95</a> , eller sende os en email på <a href="mailto:info@etdanmark.com" className="font-bold text-stone-800 whitespace-nowrap">info@etdanmark.com</a> , så vender vi tilbage hurtigst muligt.
              </p>
            </div>

            {/* Right Col (7 cols): Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm
                title="Kontakt os"
                subtitle="Du kan let og hurtigt sende os en besked i nedenstående formular."
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
